import React, { useState, useEffect, useMemo, useRef } from 'react';
import { motion, AnimatePresence } from 'motion/react';
import {
  Headphones, Bot, Layers, ChevronDown, Check, ArrowRight, MessageCircle,
  TrendingDown, Zap, Expand, Handshake, Sparkles, ShieldCheck, Globe2,
  Calculator, Send, CheckCircle2, Phone, Mail, Building2, User, Star,
} from 'lucide-react';
import Header from '../components/Header';
import Footer from '../components/Footer';
import { toast } from 'sonner';
import {
  OFFERINGS, DELIVERY_MODELS, INDUSTRIES, VALUE_POINTS, IMAGES,
  WHATSAPP_NUMBER, SALES_EMAIL,
} from './offeringsData';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const CORAL = '#fe5959';
const DARK = '#0a0a0f';

const ICONS = { Headphones, Bot, Layers, TrendingDown, Zap, Expand, Handshake };

/* ── analytics helper ── */
const track = (event, props = {}) => {
  try { window.posthog && window.posthog.capture(event, props); } catch (_) {}
};

const scrollToId = (id) => {
  const el = document.getElementById(id);
  if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
};

const buildWhatsAppUrl = (message) =>
  `https://wa.me/${WHATSAPP_NUMBER}?text=${encodeURIComponent(message)}`;

/* ─────────────── Selectable chip ─────────────── */
const Chip = ({ label, active, onToggle, testid }) => (
  <button
    type="button"
    role="checkbox"
    aria-checked={active}
    onClick={onToggle}
    data-testid={testid}
    className={`inline-flex items-center gap-1.5 text-left text-[13px] leading-snug px-3 py-2 rounded-xl border transition-all ${
      active
        ? 'border-[#fe5959] bg-[#fe5959]/10 text-[#050707] font-semibold'
        : 'border-gray-200 bg-white text-gray-600 hover:border-[#fe5959]/50'
    }`}
  >
    <span
      className={`w-4 h-4 rounded-md flex items-center justify-center flex-shrink-0 border ${
        active ? 'bg-[#fe5959] border-[#fe5959]' : 'border-gray-300'
      }`}
    >
      {active && <Check size={11} className="text-white" strokeWidth={3} />}
    </span>
    {label}
  </button>
);

/* ─────────────── Offering card ─────────────── */
const OfferingCard = ({ offering, selected, onToggleService, onInterest, delay }) => {
  const [open, setOpen] = useState(false);
  const Icon = ICONS[offering.icon] || Headphones;
  const selectedCount = offering.services.filter((s) => selected.has(`${offering.key}::${s}`)).length;

  const waMessage = `Hello Voxit Innovation. I'm interested in ${offering.headline}. I would like to discuss how Voxit can support our business.`;

  return (
    <motion.div
      initial={{ opacity: 0, y: 24 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true }}
      transition={{ duration: 0.5, delay }}
      data-testid={`offering-card-${offering.key}`}
      className="rounded-3xl bg-white border border-gray-100 shadow-[0_10px_40px_-15px_rgba(0,0,0,0.15)] overflow-hidden flex flex-col"
    >
      <div className="p-6 md:p-8 flex-1 flex flex-col">
        <div className="flex items-start justify-between gap-3 mb-4">
          <div className="w-14 h-14 rounded-2xl flex items-center justify-center" style={{ background: `${CORAL}15` }}>
            <Icon size={26} style={{ color: CORAL }} />
          </div>
          {selectedCount > 0 && (
            <span className="text-[11px] font-bold px-2.5 py-1 rounded-full text-white" style={{ background: CORAL }} data-testid={`offering-selected-count-${offering.key}`}>
              {selectedCount} selected
            </span>
          )}
        </div>

        <h3 className="text-xl font-bold text-[#050707] mb-2">{offering.headline}</h3>
        <p className="text-sm text-gray-600 leading-relaxed mb-4">{offering.short}</p>

        <div className="inline-flex items-center gap-2 text-xs font-semibold px-3 py-2 rounded-xl mb-5 self-start" style={{ background: `${CORAL}0d`, color: '#b53f3f' }}>
          <Sparkles size={13} style={{ color: CORAL }} /> {offering.savingsLabel}
        </div>

        <button
          type="button"
          onClick={() => { setOpen((o) => !o); track('offering_card_expand', { offering: offering.key, open: !open }); }}
          data-testid={`offering-toggle-${offering.key}`}
          className="w-full flex items-center justify-between text-sm font-semibold text-[#050707] py-3 border-t border-gray-100"
        >
          {open ? 'Hide details & services' : 'View details & select services'}
          <ChevronDown size={18} className={`transition-transform ${open ? 'rotate-180' : ''}`} style={{ color: CORAL }} />
        </button>

        <AnimatePresence initial={false}>
          {open && (
            <motion.div
              initial={{ height: 0, opacity: 0 }}
              animate={{ height: 'auto', opacity: 1 }}
              exit={{ height: 0, opacity: 0 }}
              transition={{ duration: 0.3 }}
              className="overflow-hidden"
            >
              <div className="pt-2 pb-1">
                <p className="text-sm text-gray-600 leading-relaxed mb-5">{offering.long}</p>

                <h4 className="text-xs font-bold uppercase tracking-wider text-gray-400 mb-3">Select the services you need</h4>
                <div className="flex flex-wrap gap-2 mb-6">
                  {offering.services.map((s) => (
                    <Chip
                      key={s}
                      label={s}
                      active={selected.has(`${offering.key}::${s}`)}
                      onToggle={() => onToggleService(offering.key, s)}
                      testid={`service-chip-${offering.key}-${s.toLowerCase().replace(/[^a-z0-9]+/g, '-')}`}
                    />
                  ))}
                </div>

                <div className="grid sm:grid-cols-2 gap-6 mb-2">
                  <div>
                    <h4 className="text-xs font-bold uppercase tracking-wider text-gray-400 mb-3">Business benefits</h4>
                    <ul className="space-y-2">
                      {offering.benefits.map((b) => (
                        <li key={b} className="flex items-start gap-2 text-[13px] text-gray-700">
                          <Check size={15} className="mt-0.5 flex-shrink-0" style={{ color: CORAL }} /> {b}
                        </li>
                      ))}
                    </ul>
                  </div>
                  <div>
                    <h4 className="text-xs font-bold uppercase tracking-wider text-gray-400 mb-3">Engagement models</h4>
                    <div className="flex flex-wrap gap-2">
                      {offering.models.map((m) => (
                        <span key={m} className="text-[12px] px-2.5 py-1 rounded-lg bg-gray-100 text-gray-700">{m}</span>
                      ))}
                    </div>
                  </div>
                </div>
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>

      <div className="p-4 border-t border-gray-100 bg-gray-50/60 flex flex-col sm:flex-row gap-2">
        <button
          type="button"
          onClick={() => { onInterest(offering); track('offering_interest_click', { offering: offering.key }); }}
          data-testid={`offering-interest-${offering.key}`}
          className="flex-1 text-white text-sm font-semibold py-3 rounded-xl transition-colors flex items-center justify-center gap-2"
          style={{ background: CORAL }}
        >
          I'm interested <ArrowRight size={16} />
        </button>
        <a
          href={buildWhatsAppUrl(waMessage)}
          target="_blank"
          rel="noopener noreferrer"
          onClick={() => track('whatsapp_click', { source: `card_${offering.key}` })}
          data-testid={`offering-whatsapp-${offering.key}`}
          className="flex-1 text-sm font-semibold py-3 rounded-xl border border-gray-200 bg-white text-[#050707] hover:border-[#25D366] hover:text-[#128C7E] transition-colors flex items-center justify-center gap-2"
        >
          <MessageCircle size={16} /> WhatsApp
        </a>
      </div>
    </motion.div>
  );
};

/* ─────────────── Savings estimator ─────────────── */
const Estimator = () => {
  const [employees, setEmployees] = useState('');
  const [monthlyCost, setMonthlyCost] = useState('');
  const [serviceType, setServiceType] = useState('');
  const [volume, setVolume] = useState('');
  const [model, setModel] = useState('');
  const [result, setResult] = useState(null);

  const compute = () => {
    const cost = parseFloat(monthlyCost);
    if (!serviceType || !cost || cost <= 0) { setResult({ insufficient: true }); track('estimator_run', { insufficient: true }); return; }
    const low = Math.round(cost * 0.2);
    const high = Math.round(cost * 0.55);
    const vol = parseFloat(volume) || 0;
    const hours = Math.round(vol * 0.15 + (parseFloat(employees) || 0) * 12);
    const suggestion = serviceType === 'AI & Business Automation'
      ? 'Start with a paid AI opportunity assessment or a single automation pilot.'
      : serviceType === 'Digital Product Development'
      ? 'Start with a product discovery workshop or one dedicated specialist.'
      : 'Start with a managed after-hours or overflow support pilot.';
    setResult({ low, high, hours, suggestion });
    track('estimator_run', { serviceType, model, insufficient: false });
  };

  const money = (n) => `K${n.toLocaleString('en-GB')}`;
  const field = 'w-full px-4 py-3 rounded-xl border border-gray-200 text-sm text-[#050707] outline-none focus:border-[#fe5959] transition-colors bg-white';

  return (
    <div className="rounded-3xl bg-white border border-gray-100 shadow-[0_10px_40px_-15px_rgba(0,0,0,0.2)] p-6 md:p-8" data-testid="savings-estimator">
      <div className="grid lg:grid-cols-2 gap-8">
        <div className="space-y-4">
          <div>
            <label className="block text-xs font-bold uppercase tracking-wider text-gray-400 mb-2">Employees in the relevant operation</label>
            <input type="number" min="0" value={employees} onChange={(e) => setEmployees(e.target.value)} placeholder="e.g. 8" className={field} data-testid="estimator-employees" />
          </div>
          <div>
            <label className="block text-xs font-bold uppercase tracking-wider text-gray-400 mb-2">Approximate monthly operating cost (ZMW)</label>
            <input type="number" min="0" value={monthlyCost} onChange={(e) => setMonthlyCost(e.target.value)} placeholder="e.g. 40000" className={field} data-testid="estimator-cost" />
          </div>
          <div>
            <label className="block text-xs font-bold uppercase tracking-wider text-gray-400 mb-2">Type of service</label>
            <select value={serviceType} onChange={(e) => setServiceType(e.target.value)} className={field} data-testid="estimator-service">
              <option value="">Select a service</option>
              {OFFERINGS.map((o) => <option key={o.key} value={o.headline}>{o.headline}</option>)}
            </select>
          </div>
          <div>
            <label className="block text-xs font-bold uppercase tracking-wider text-gray-400 mb-2">Current monthly support volume / tasks</label>
            <input type="number" min="0" value={volume} onChange={(e) => setVolume(e.target.value)} placeholder="e.g. 1200" className={field} data-testid="estimator-volume" />
          </div>
          <div>
            <label className="block text-xs font-bold uppercase tracking-wider text-gray-400 mb-2">Preferred delivery model</label>
            <select value={model} onChange={(e) => setModel(e.target.value)} className={field} data-testid="estimator-model">
              <option value="">Select a model</option>
              {DELIVERY_MODELS.map((m) => <option key={m.name} value={m.name}>{m.name}</option>)}
            </select>
          </div>
          <button type="button" onClick={compute} data-testid="estimator-calculate" className="w-full text-white text-sm font-semibold py-3.5 rounded-xl flex items-center justify-center gap-2" style={{ background: CORAL }}>
            <Calculator size={16} /> Estimate my potential saving
          </button>
        </div>

        <div className="rounded-2xl p-6 flex flex-col justify-center" style={{ background: DARK }}>
          {!result && (
            <div className="text-center text-gray-400">
              <Calculator size={34} className="mx-auto mb-3" style={{ color: CORAL }} />
              <p className="text-sm">Fill in the details and we'll show an indicative potential saving range.</p>
            </div>
          )}
          {result?.insufficient && (
            <div className="text-center" data-testid="estimator-insufficient">
              <p className="text-white text-sm mb-4">We need a little more information to give a reliable estimate.</p>
              <button onClick={() => scrollToId('interest-form')} className="text-white text-sm font-semibold px-5 py-2.5 rounded-xl" style={{ background: CORAL }}>
                Request a free assessment instead
              </button>
            </div>
          )}
          {result && !result.insufficient && (
            <div data-testid="estimator-result">
              <span className="text-xs font-bold uppercase tracking-wider" style={{ color: CORAL }}>Indicative potential saving</span>
              <div className="text-3xl md:text-4xl font-bold text-white mt-2 mb-1">{money(result.low)} – {money(result.high)}</div>
              <p className="text-xs text-gray-400 mb-5">estimated potential monthly range</p>
              <div className="grid grid-cols-2 gap-3 mb-5">
                <div className="rounded-xl p-3 bg-white/5">
                  <div className="text-xl font-bold text-white">~{result.hours.toLocaleString('en-GB')}</div>
                  <div className="text-[11px] text-gray-400">potential hours saved / month</div>
                </div>
                <div className="rounded-xl p-3 bg-white/5">
                  <div className="text-xl font-bold text-white">20–55%</div>
                  <div className="text-[11px] text-gray-400">possible cost reduction</div>
                </div>
              </div>
              <p className="text-[13px] text-gray-300 mb-4"><strong className="text-white">Suggested start:</strong> {result.suggestion}</p>
              <button onClick={() => scrollToId('interest-form')} className="w-full text-white text-sm font-semibold py-3 rounded-xl" style={{ background: CORAL }}>
                Validate this with a free assessment
              </button>
            </div>
          )}
        </div>
      </div>
      <p className="text-xs text-gray-500 mt-5 leading-relaxed">
        This is an initial estimate only. Actual savings depend on your existing costs, processes, volumes, service requirements, and implementation scope.
      </p>
    </div>
  );
};

/* ─────────────── Interest form ─────────────── */
const InterestForm = ({ selectedServices, selectedOfferings }) => {
  const [form, setForm] = useState({
    full_name: '', company: '', email: '', phone: '', preferred_contact: 'WhatsApp',
    need: '', employees: '', industry: '', delivery_model: '', budget: '', preferred_time: '',
    consent: false, honeypot: '',
  });
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const startedRef = useRef(false);
  const loadTime = useRef(Date.now());

  const onChange = (e) => {
    const { name, value, type, checked } = e.target;
    if (!startedRef.current) { startedRef.current = true; track('form_start'); }
    setForm((p) => ({ ...p, [name]: type === 'checkbox' ? checked : value }));
  };

  const validEmail = (e) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(e);

  const waMessageAfter = useMemo(() => {
    const list = selectedServices.length ? selectedServices.map((s) => s.service).join(', ') : selectedOfferings.join(', ') || 'your services';
    return `Hello Voxit Innovation. I'm interested in ${list}. My name is ${form.full_name || '[name]'} from ${form.company || '[company]'}. I would like to discuss how Voxit can support our business.`;
  }, [selectedServices, selectedOfferings, form.full_name, form.company]);

  const submit = async (e) => {
    e.preventDefault();
    if (Date.now() - loadTime.current < 3000) { toast.error('Please take a moment to complete the form.'); return; }
    if (!form.full_name.trim() || !form.company.trim() || !form.email.trim() || !form.phone.trim() || !form.need.trim()) {
      toast.error('Please fill in all required fields.'); return;
    }
    if (!validEmail(form.email)) { toast.error('Please enter a valid work email.'); return; }
    if (!form.consent) { toast.error('Please provide consent so we can contact you.'); return; }

    setLoading(true);
    try {
      const payload = {
        ...form,
        offerings: selectedOfferings,
        services: selectedServices.map((s) => s.service),
      };
      const res = await fetch(`${API_URL}/api/leads/offerings`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });
      if (res.ok) {
        setSubmitted(true);
        track('form_submit', { offerings: selectedOfferings.length, services: selectedServices.length });
        toast.success('Request received — we will be in touch shortly.');
      } else {
        const err = await res.json().catch(() => ({}));
        toast.error(err.detail || 'Submission failed. Please try again.');
      }
    } catch {
      toast.error('Network error. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const field = 'w-full px-4 py-3 rounded-xl border border-gray-200 text-sm text-[#050707] outline-none focus:border-[#fe5959] transition-colors bg-white';
  const labelCls = 'block text-xs font-bold uppercase tracking-wider text-gray-400 mb-2';

  if (submitted) {
    return (
      <motion.div initial={{ scale: 0.95, opacity: 0 }} animate={{ scale: 1, opacity: 1 }} className="rounded-3xl bg-white border border-gray-100 shadow-lg p-8 md:p-12 text-center max-w-xl mx-auto" data-testid="interest-form-success">
        <div className="w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-5" style={{ background: `${CORAL}15` }}>
          <CheckCircle2 size={32} style={{ color: CORAL }} />
        </div>
        <h3 className="text-2xl font-bold text-[#050707] mb-3">Thank you. Your request has been received.</h3>
        <p className="text-gray-600 mb-7">A Voxit representative will contact you shortly.</p>
        <a
          href={buildWhatsAppUrl(waMessageAfter)}
          target="_blank"
          rel="noopener noreferrer"
          onClick={() => track('whatsapp_click', { source: 'form_success' })}
          data-testid="success-whatsapp-btn"
          className="inline-flex items-center gap-2 text-white text-sm font-semibold px-7 py-3.5 rounded-xl"
          style={{ background: '#25D366' }}
        >
          <MessageCircle size={18} /> Continue on WhatsApp
        </a>
      </motion.div>
    );
  }

  return (
    <form onSubmit={submit} className="rounded-3xl bg-white border border-gray-100 shadow-[0_10px_40px_-15px_rgba(0,0,0,0.2)] p-6 md:p-8" data-testid="interest-form">
      <div style={{ position: 'absolute', left: '-9999px', opacity: 0, height: 0 }} aria-hidden="true">
        <input type="text" name="honeypot" value={form.honeypot} onChange={onChange} tabIndex={-1} autoComplete="off" />
      </div>

      {/* selected summary */}
      <div className="mb-6 rounded-2xl p-4 bg-gray-50 border border-gray-100" data-testid="form-selected-summary">
        <span className="text-xs font-bold uppercase tracking-wider text-gray-400">Your selected services</span>
        {selectedServices.length === 0 ? (
          <p className="text-sm text-gray-500 mt-2">No services selected yet — pick from the offering cards above, or just describe your need below.</p>
        ) : (
          <div className="flex flex-wrap gap-2 mt-2">
            {selectedServices.map((s) => (
              <span key={`${s.offering}::${s.service}`} className="text-[12px] px-2.5 py-1 rounded-lg text-white" style={{ background: CORAL }}>{s.service}</span>
            ))}
          </div>
        )}
      </div>

      <div className="grid sm:grid-cols-2 gap-4">
        <div>
          <label className={labelCls}>Full name *</label>
          <input name="full_name" value={form.full_name} onChange={onChange} className={field} placeholder="Your full name" data-testid="form-full-name" required />
        </div>
        <div>
          <label className={labelCls}>Company name *</label>
          <input name="company" value={form.company} onChange={onChange} className={field} placeholder="Your company" data-testid="form-company" required />
        </div>
        <div>
          <label className={labelCls}>Work email *</label>
          <input type="email" name="email" value={form.email} onChange={onChange} className={field} placeholder="you@company.com" data-testid="form-email" required />
        </div>
        <div>
          <label className={labelCls}>Phone number *</label>
          <input type="tel" name="phone" value={form.phone} onChange={onChange} className={field} placeholder="+260 ..." data-testid="form-phone" required />
        </div>
      </div>

      <div className="mt-4">
        <label className={labelCls}>Preferred contact method</label>
        <div className="flex flex-wrap gap-2">
          {['WhatsApp', 'Phone', 'Email'].map((m) => (
            <button key={m} type="button" onClick={() => setForm((p) => ({ ...p, preferred_contact: m }))} data-testid={`form-contact-${m.toLowerCase()}`}
              className={`text-sm px-4 py-2 rounded-xl border transition-all ${form.preferred_contact === m ? 'border-[#fe5959] bg-[#fe5959]/10 text-[#050707] font-semibold' : 'border-gray-200 text-gray-600'}`}>
              {m}
            </button>
          ))}
        </div>
      </div>

      <div className="mt-4">
        <label className={labelCls}>Short description of your business need *</label>
        <textarea name="need" value={form.need} onChange={onChange} rows={4} className={field} placeholder="Tell us what you'd like to improve..." data-testid="form-need" required />
      </div>

      {/* optional */}
      <details className="mt-4 group">
        <summary className="cursor-pointer text-sm font-semibold text-[#050707] flex items-center gap-2 select-none">
          <ChevronDown size={16} className="group-open:rotate-180 transition-transform" style={{ color: CORAL }} /> Optional details (helps us prepare)
        </summary>
        <div className="grid sm:grid-cols-2 gap-4 mt-4">
          <div>
            <label className={labelCls}>Number of employees</label>
            <input name="employees" value={form.employees} onChange={onChange} className={field} placeholder="e.g. 25" data-testid="form-employees" />
          </div>
          <div>
            <label className={labelCls}>Industry</label>
            <select name="industry" value={form.industry} onChange={onChange} className={field} data-testid="form-industry">
              <option value="">Select industry</option>
              {INDUSTRIES.map((i) => <option key={i} value={i}>{i}</option>)}
            </select>
          </div>
          <div>
            <label className={labelCls}>Preferred delivery model</label>
            <select name="delivery_model" value={form.delivery_model} onChange={onChange} className={field} data-testid="form-delivery-model">
              <option value="">Select model</option>
              {DELIVERY_MODELS.map((m) => <option key={m.name} value={m.name}>{m.name}</option>)}
            </select>
          </div>
          <div>
            <label className={labelCls}>Approximate monthly budget</label>
            <input name="budget" value={form.budget} onChange={onChange} className={field} placeholder="e.g. K20,000" data-testid="form-budget" />
          </div>
          <div>
            <label className={labelCls}>Preferred contact time</label>
            <input name="preferred_time" value={form.preferred_time} onChange={onChange} className={field} placeholder="e.g. weekday mornings" data-testid="form-preferred-time" />
          </div>
        </div>
      </details>

      <label className="flex items-start gap-3 mt-6 cursor-pointer">
        <input type="checkbox" name="consent" checked={form.consent} onChange={onChange} className="mt-0.5 w-4 h-4 accent-[#fe5959]" data-testid="form-consent" />
        <span className="text-[13px] text-gray-600">I agree that Voxit Innovation may contact me regarding this enquiry.</span>
      </label>

      <button type="submit" disabled={loading} className="w-full mt-6 text-white text-sm font-semibold py-4 rounded-xl flex items-center justify-center gap-2 disabled:opacity-60" style={{ background: CORAL }} data-testid="form-submit">
        {loading ? <span className="w-5 h-5 border-2 border-white/40 border-t-white rounded-full animate-spin" /> : <><Send size={16} /> Request My Free Assessment</>}
      </button>
    </form>
  );
};

/* ─────────────── Page ─────────────── */
const OfferingsPage = () => {
  const [selected, setSelected] = useState(new Set());

  useEffect(() => {
    const prevTitle = document.title;
    document.title = 'Business Support, AI Automation & Product Development Zambia | Voxit';
    const setMeta = (name, content) => {
      let el = document.querySelector(`meta[name="${name}"]`);
      if (!el) { el = document.createElement('meta'); el.setAttribute('name', name); document.head.appendChild(el); }
      el.setAttribute('content', content);
    };
    const prevDesc = document.querySelector('meta[name="description"]')?.getAttribute('content');
    setMeta('description', "Explore Voxit Innovation's customer support, BPO, AI automation, and digital product development solutions for businesses in Zambia. Request a free assessment.");
    track('offerings_page_view');
    return () => { document.title = prevTitle; if (prevDesc) setMeta('description', prevDesc); };
  }, []);

  const toggleService = (offeringKey, service) => {
    setSelected((prev) => {
      const next = new Set(prev);
      const id = `${offeringKey}::${service}`;
      if (next.has(id)) next.delete(id); else { next.add(id); track('service_select', { offering: offeringKey, service }); }
      return next;
    });
  };

  const selectedServices = useMemo(
    () => Array.from(selected).map((id) => { const [offering, ...rest] = id.split('::'); return { offering, service: rest.join('::') }; }),
    [selected]
  );
  const selectedOfferings = useMemo(() => {
    const keys = new Set(selectedServices.map((s) => s.offering));
    return OFFERINGS.filter((o) => keys.has(o.key)).map((o) => o.headline);
  }, [selectedServices]);

  const onInterest = (offering) => {
    setSelected((prev) => {
      const next = new Set(prev);
      // if nothing from this offering selected, pre-select its first service
      const anySelected = offering.services.some((s) => next.has(`${offering.key}::${s}`));
      if (!anySelected) next.add(`${offering.key}::${offering.services[0]}`);
      return next;
    });
    scrollToId('interest-form');
  };

  const heroWa = 'Hello Voxit Innovation. I would like to discuss how Voxit can support our business.';

  return (
    <div className="bg-white">
      <Header />

      {/* Sticky mobile WhatsApp */}
      <a
        href={buildWhatsAppUrl(heroWa)}
        target="_blank"
        rel="noopener noreferrer"
        onClick={() => track('whatsapp_click', { source: 'sticky_mobile' })}
        data-testid="sticky-whatsapp"
        className="md:hidden fixed bottom-4 right-4 z-40 inline-flex items-center gap-2 text-white text-sm font-semibold px-5 py-3.5 rounded-full shadow-xl"
        style={{ background: '#25D366' }}
      >
        <MessageCircle size={18} /> Chat with us
      </a>

      {/* 2. HERO */}
      <section className="relative pt-32 pb-16 md:pt-40 md:pb-24 overflow-hidden" style={{ background: DARK }}>
        <div className="absolute inset-0 opacity-40" style={{ background: `radial-gradient(circle at 15% 20%, ${CORAL}30 0%, transparent 45%)` }} />
        <div className="container-custom relative z-10">
          <div className="grid lg:grid-cols-2 gap-10 items-center">
            <motion.div initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }}>
              <span className="inline-flex items-center gap-2 text-xs font-semibold px-3 py-1.5 rounded-full mb-5" style={{ background: `${CORAL}20`, color: '#ff8f8f' }}>
                <Globe2 size={13} /> Swedish technology · Zambian local expertise
              </span>
              <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-white leading-[1.1] mb-5">
                Smarter Operations. Better Service. Lower Costs.
              </h1>
              <p className="text-base md:text-lg text-gray-300 mb-6 max-w-xl leading-relaxed">
                Voxit Innovation helps businesses in Zambia improve customer support, automate daily operations, and build digital products—all through flexible solutions designed around your organization.
              </p>
              <div className="flex items-center gap-3 rounded-2xl px-5 py-4 mb-5 max-w-xl" style={{ background: 'rgba(255,255,255,0.05)', border: '1px solid rgba(255,255,255,0.1)' }} data-testid="hero-swe-zam-line">
                <span className="text-xl leading-none flex-shrink-0" aria-hidden="true">🇸🇪&nbsp;🇿🇲</span>
                <p className="text-sm text-gray-200 leading-relaxed">
                  We combine <strong className="text-white">world-class Swedish technology expertise</strong> with <strong className="text-white">world-class Zambian local knowledge</strong>—and that is exactly what makes it work so well.
                </p>
              </div>
              <div className="inline-flex flex-col gap-1 rounded-2xl px-5 py-4 mb-7" style={{ background: 'rgba(255,255,255,0.06)', border: '1px solid rgba(255,255,255,0.1)' }}>
                <span className="text-lg font-bold text-white">Reduce relevant operational costs by an estimated 20–55%.</span>
                <span className="text-xs text-gray-400">Depending on your current setup and selected solution.</span>
              </div>
              <div className="flex flex-col sm:flex-row gap-3 mb-6">
                <button onClick={() => scrollToId('offerings')} data-testid="hero-explore-btn" className="text-white text-sm font-semibold px-7 py-3.5 rounded-full flex items-center justify-center gap-2" style={{ background: CORAL }}>
                  Explore Our Offerings <ArrowRight size={16} />
                </button>
                <a href={buildWhatsAppUrl(heroWa)} target="_blank" rel="noopener noreferrer" onClick={() => track('whatsapp_click', { source: 'hero' })} data-testid="hero-whatsapp-btn" className="text-sm font-semibold px-7 py-3.5 rounded-full flex items-center justify-center gap-2 text-white" style={{ background: '#25D366' }}>
                  <MessageCircle size={16} /> Talk to Us on WhatsApp
                </a>
              </div>
              <div className="flex flex-col gap-1 text-sm text-gray-400">
                <span className="flex items-center gap-2"><ShieldCheck size={15} style={{ color: CORAL }} /> International experience. Local delivery. Flexible engagement.</span>
                <span className="text-gray-500 text-[13px] pl-6">From one specialist to a complete managed operation.</span>
              </div>
            </motion.div>

            <motion.div initial={{ opacity: 0, scale: 0.96 }} animate={{ opacity: 1, scale: 1 }} transition={{ duration: 0.6, delay: 0.15 }} className="hidden lg:block">
              <div className="relative rounded-3xl overflow-hidden shadow-2xl" style={{ aspectRatio: '4/3' }}>
                <img src={IMAGES.hero} alt="Zambian business professionals collaborating" className="w-full h-full object-cover" loading="eager" />
                <div className="absolute inset-0" style={{ background: 'linear-gradient(180deg, transparent 40%, rgba(10,10,15,0.5) 100%)' }} />
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* 3. VALUE SECTION */}
      <section className="py-16 md:py-24">
        <div className="container-custom">
          <div className="max-w-2xl mb-12">
            <h2 className="text-3xl md:text-4xl font-bold text-[#050707] mb-4">Focus on your business. We handle the complexity.</h2>
            <p className="text-gray-600 leading-relaxed">
              Whether you need better customer support, intelligent automation, or a complete digital platform, Voxit can provide the expertise, technology, management, maintenance, and ongoing support needed to deliver measurable results.
            </p>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
            {VALUE_POINTS.map((v, i) => {
              const Icon = ICONS[v.icon] || TrendingDown;
              return (
                <motion.div key={v.title} initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: i * 0.08 }} className="rounded-2xl border border-gray-100 p-6 bg-white shadow-sm">
                  <div className="w-12 h-12 rounded-xl flex items-center justify-center mb-4" style={{ background: `${CORAL}15` }}>
                    <Icon size={22} style={{ color: CORAL }} />
                  </div>
                  <h3 className="text-base font-bold text-[#050707]">{v.title}</h3>
                </motion.div>
              );
            })}
          </div>

          {/* Special Zambia offering */}
          <div className="mt-10 rounded-3xl p-6 md:p-8 flex flex-col md:flex-row items-start md:items-center gap-5" style={{ background: `${CORAL}0d`, border: `1px solid ${CORAL}33` }} data-testid="zambia-special-offering">
            <div className="w-14 h-14 rounded-2xl flex items-center justify-center flex-shrink-0 text-white" style={{ background: CORAL }}>
              <Sparkles size={26} />
            </div>
            <div>
              <h3 className="text-lg font-bold text-[#050707] mb-1">Special solutions for Zambia's small and medium-sized businesses</h3>
              <p className="text-sm text-gray-600">A dedicated offering developed for Zambia's SME community, with pricing and delivery models designed for local businesses.</p>
            </div>
          </div>
        </div>
      </section>

      {/* 4. OFFERINGS */}
      <section id="offerings" className="py-16 md:py-24 bg-gray-50/60 scroll-mt-24">
        <div className="container-custom">
          <div className="max-w-2xl mb-12">
            <span className="text-xs font-bold uppercase tracking-wider" style={{ color: CORAL }}>Our offerings</span>
            <h2 className="text-3xl md:text-4xl font-bold text-[#050707] mt-2 mb-3">Choose what your business needs</h2>
            <p className="text-gray-600">Select one or several services across the cards below. Your choices are carried into the enquiry form and WhatsApp message.</p>
          </div>
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 items-start">
            {OFFERINGS.map((o, i) => (
              <OfferingCard key={o.key} offering={o} selected={selected} onToggleService={toggleService} onInterest={onInterest} delay={i * 0.1} />
            ))}
          </div>
        </div>
      </section>

      {/* 5. DELIVERY MODELS */}
      <section className="py-16 md:py-24">
        <div className="container-custom">
          <div className="max-w-2xl mb-10">
            <h2 className="text-3xl md:text-4xl font-bold text-[#050707] mb-4">Choose the model that fits your business.</h2>
            <p className="text-gray-600 leading-relaxed">
              You do not need to replace your entire operation at once. Start with one specialist, one process, or one pilot project—and scale when the solution proves its value.
            </p>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-4">
            {DELIVERY_MODELS.map((m, i) => (
              <motion.div key={m.name} initial={{ opacity: 0, y: 16 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: i * 0.06 }} className="rounded-2xl border border-gray-100 p-5 bg-white shadow-sm">
                <div className="text-xs font-bold uppercase tracking-wider mb-2" style={{ color: CORAL }}>0{i + 1}</div>
                <h3 className="text-sm font-bold text-[#050707] mb-2">{m.name}</h3>
                <p className="text-[13px] text-gray-600 mb-3 leading-relaxed">{m.desc}</p>
                <p className="text-[11px] text-gray-400"><Star size={11} className="inline -mt-0.5 mr-1" style={{ color: CORAL }} />Best for: {m.best}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* 6. ESTIMATOR */}
      <section className="py-16 md:py-24 bg-gray-50/60">
        <div className="container-custom">
          <div className="max-w-2xl mb-10">
            <span className="text-xs font-bold uppercase tracking-wider" style={{ color: CORAL }}>Savings & value estimator</span>
            <h2 className="text-3xl md:text-4xl font-bold text-[#050707] mt-2 mb-3">See your potential savings</h2>
            <p className="text-gray-600">Potential operational cost reduction of 20–55%, depending on your current setup and selected solution. Estimated savings are indicative and will be validated through an initial assessment of your current operations.</p>
          </div>
          <Estimator />
        </div>
      </section>

      {/* 7. EXPERIENCE */}
      <section className="py-16 md:py-24">
        <div className="container-custom">
          <div className="grid lg:grid-cols-2 gap-10 items-center">
            <div>
              <h2 className="text-3xl md:text-4xl font-bold text-[#050707] mb-4">Experience across industries and markets</h2>
              <p className="text-gray-600 leading-relaxed mb-7">
                Our team has delivered support, automation, consulting, and digital solutions across the Nordic region and Zambia. This cross-market experience allows us to combine international delivery standards with practical understanding of local business needs.
              </p>
              <div className="flex flex-wrap gap-2">
                {INDUSTRIES.map((ind) => (
                  <span key={ind} className="text-sm px-4 py-2 rounded-xl border border-gray-200 text-gray-700 bg-white">{ind}</span>
                ))}
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              {[IMAGES.shop, IMAGES.support, IMAGES.farm, IMAGES.product].map((src, i) => (
                <motion.div key={i} initial={{ opacity: 0, scale: 0.95 }} whileInView={{ opacity: 1, scale: 1 }} viewport={{ once: true }} transition={{ delay: i * 0.08 }} className={`rounded-2xl overflow-hidden shadow-sm ${i % 2 === 0 ? 'mt-0' : 'mt-6'}`} style={{ aspectRatio: '3/4' }}>
                  <img src={src} alt="Voxit experience across industries" className="w-full h-full object-cover" loading="lazy" />
                </motion.div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* 8. INTEREST FORM */}
      <section id="interest-form" className="py-16 md:py-24 bg-gray-50/60 scroll-mt-24">
        <div className="container-custom max-w-3xl">
          <div className="text-center mb-10">
            <h2 className="text-3xl md:text-4xl font-bold text-[#050707] mb-3">Tell us what your business needs.</h2>
            <p className="text-gray-600">Your selected services are included automatically. We'll respond with a free, no-obligation assessment.</p>
          </div>
          <InterestForm selectedServices={selectedServices} selectedOfferings={selectedOfferings} />
        </div>
      </section>

      {/* 9. FINAL CTA */}
      <section className="py-16 md:py-24" style={{ background: DARK }}>
        <div className="container-custom text-center">
          <h2 className="text-3xl md:text-4xl font-bold text-white mb-4">Ready to make your business more efficient?</h2>
          <p className="text-gray-300 max-w-2xl mx-auto mb-8 leading-relaxed">
            Start with a free conversation. We will review your current operation, identify improvement opportunities, and recommend a practical way forward.
          </p>
          <div className="flex flex-col sm:flex-row gap-3 justify-center mb-5">
            <button onClick={() => scrollToId('interest-form')} data-testid="final-assessment-btn" className="text-white text-sm font-semibold px-8 py-4 rounded-full flex items-center justify-center gap-2" style={{ background: CORAL }}>
              Request a Free Assessment <ArrowRight size={16} />
            </button>
            <a href={buildWhatsAppUrl(heroWa)} target="_blank" rel="noopener noreferrer" onClick={() => track('whatsapp_click', { source: 'final_cta' })} data-testid="final-whatsapp-btn" className="text-white text-sm font-semibold px-8 py-4 rounded-full flex items-center justify-center gap-2" style={{ background: '#25D366' }}>
              <MessageCircle size={16} /> Chat on WhatsApp
            </a>
          </div>
          <p className="text-sm text-gray-500">No obligation. Flexible scope. Solutions designed around your business.</p>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default OfferingsPage;
